import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import {
  PanelLeftClose,
  PanelLeftOpen,
  Download,
  ArrowLeft,
} from 'lucide-react';
import { getPageVisual, renderPageIcon } from './pageVisuals';

interface PageInfo {
  page: number;
  label: string;
  sheetName?: string;
}

interface PageIndexSidebarProps {
  numPages: number | null;
  pageNumber: number;
  pageIndex?: PageInfo[] | null;
  annotationCountsByPage?: Record<number, number>;
  pdfDocument: any;
  onNavigateToPage: (page: number) => void;
  onDownloadPDF?: () => void;
  onHomeClick?: () => void;
}

const BADGE_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  power: { bg: 'var(--green-tint-medium)', color: 'var(--green-text)', border: 'var(--green-tint-strong)' },
  lighting: { bg: 'var(--yellow-tint-medium)', color: 'var(--yellow-text)', border: 'var(--yellow-tint-strong)' },
  'reflected ceiling plan': { bg: 'var(--yellow-tint-medium)', color: 'var(--yellow-text)', border: 'var(--yellow-tint-strong)' },
  rcp: { bg: 'var(--yellow-tint-medium)', color: 'var(--yellow-text)', border: 'var(--yellow-tint-strong)' },
  demolition: { bg: 'var(--white-tint-medium)', color: 'var(--red-text)', border: 'var(--red-tint-strong)' },
  demo: { bg: 'var(--white-tint-medium)', color: 'var(--red-text)', border: 'var(--red-tint-strong)' },
  'fire alarm': { bg: 'var(--red-tint-medium)', color: 'var(--yellow-text-dark)', border: 'var(--yellow-tint-strong)' },
  'low voltage': { bg: 'var(--purple-tint-medium)', color: 'var(--purple-text)', border: 'var(--purple-tint-strong)' },
  telecom: { bg: 'var(--purple-tint-medium)', color: 'var(--purple-text)', border: 'var(--purple-tint-strong)' },
  panel: { bg: 'var(--blue-tint-medium)', color: 'var(--cyan-text-dark)', border: 'var(--blue-tint-strong)' },
};

function getBadgeColors(label: string | undefined) {
  if (!label) return null;
  const lower = label.toLowerCase();
  for (const [key, colors] of Object.entries(BADGE_COLORS)) {
    if (lower.includes(key)) return colors;
  }
  return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'var(--border-color)' };
}

interface PageListItemProps {
  page: number;
  annotationCount: number;
  isCurrent: boolean;
  isOpen: boolean;
  sheetName?: string;
  label?: string;
  onNavigateToPage: (page: number) => void;
}

const PageListItem = memo<PageListItemProps>(
  ({
    page,
    annotationCount,
    isCurrent,
    isOpen,
    sheetName,
    label,
    onNavigateToPage,
  }) => {
    const handleClick = useCallback(() => {
      onNavigateToPage(page);
    }, [onNavigateToPage, page]);

    const badgeColors = getBadgeColors(label);
    const visual = getPageVisual(label);

    return (
      <button
        className={`pis-item${isCurrent ? ' active' : ''}`}
        onClick={handleClick}
        title={isOpen ? undefined : `Page ${page}${sheetName ? ` · ${sheetName}` : ''}`}
      >
        {/* Thumbnail — takes up most of the card */}
        <div className="pis-thumb-wrapper">
          <div
            style={{
              position: 'relative',
              width: '100%',
              paddingBottom: '70%',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderPageIcon(visual.type, visual.iconColor)}
            </div>
          </div>
          <span className="pis-page-num">{page}</span>
          {annotationCount > 0 && (
            <span className="pis-annotation-count" title={`${annotationCount} annotation${annotationCount === 1 ? '' : 's'} on page ${page}`}>
              {annotationCount}
            </span>
          )}
          {isCurrent && <span className="pis-active-dot" />}
        </div>

        {/* Title + badge stacked below thumbnail */}
        <div className="pis-info">
          <span className="pis-page-title">
            {sheetName || `Page ${page}`}
          </span>
          {badgeColors && label && (
            <span
              className="pis-badge"
              style={{
                color: badgeColors.color,
                background: badgeColors.bg,
                border: `1px solid ${badgeColors.border}`,
              }}
            >
              {label}
            </span>
          )}
        </div>
      </button>
    );
  },
  (prev, next) =>
    prev.page === next.page &&
    prev.annotationCount === next.annotationCount &&
    prev.isCurrent === next.isCurrent &&
    prev.isOpen === next.isOpen &&
    prev.sheetName === next.sheetName &&
    prev.label === next.label &&
    prev.onNavigateToPage === next.onNavigateToPage
);

const PageIndexSidebar: React.FC<PageIndexSidebarProps> = ({
  numPages,
  pageNumber,
  pageIndex,
  annotationCountsByPage,
  pdfDocument: _pdfDocument,
  onNavigateToPage,
  onDownloadPDF,
  onHomeClick,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const pageNumbers = useMemo(
    () => (numPages && numPages > 0 ? Array.from({ length: numPages }, (_, i) => i + 1) : []),
    [numPages]
  );
  const pageInfoByPage = useMemo(() => {
    const map = new Map<number, PageInfo>();
    if (pageIndex) {
      for (const info of pageIndex) map.set(info.page, info);
    }
    return map;
  }, [pageIndex]);

  // Scroll active page into view when page changes
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('.pis-item.active') as HTMLElement;
    if (active) {
      // Avoid smooth scroll animation to prevent enqueuing many transient rows.
      active.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [pageNumber]);

  return (
    <div className={`pis-root${isOpen ? '' : ' collapsed'}`}>
      {/* Header with toggle */}
      <div className="pis-header">
        {onHomeClick && (
          <button
            className="pis-toggle"
            onClick={onHomeClick}
            title="Back to Home"
            style={{ marginRight: isOpen ? 2 : 0 }}
          >
            <ArrowLeft size={13} />
          </button>
        )}
        {isOpen && (
          <>
            <span className="pis-title">Pages</span>
            <span className="pis-count">{numPages || 0}</span>
          </>
        )}
        {isOpen && onDownloadPDF && (
          <button
            className="pis-toggle"
            onClick={onDownloadPDF}
            title="Download PDF"
            style={{ marginRight: 2 }}
          >
            <Download size={13} />
          </button>
        )}
        <button
          className="pis-toggle"
          onClick={() => setIsOpen((o) => !o)}
          title={isOpen ? 'Collapse pages' : 'Expand pages'}
        >
          {isOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
        </button>
      </div>

      {/* Page list */}
      <div className="pis-list" ref={listRef}>
        {pageNumbers.length > 0 ? (
          pageNumbers.map((pg) => {
            const info = pageInfoByPage.get(pg);
            return (
              <PageListItem
                key={pg}
                page={pg}
                annotationCount={annotationCountsByPage?.[pg] || 0}
                isCurrent={pg === pageNumber}
                isOpen={isOpen}
                sheetName={info?.sheetName}
                label={info?.label}
                onNavigateToPage={onNavigateToPage}
              />
            );
          })
        ) : (
          <div className="pis-empty">No pages</div>
        )}
      </div>
    </div>
  );
};

export default PageIndexSidebar;
